import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, MessageCircle, Loader2 } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null); // user object
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => { const { data } = await api.get("/messages/conversations"); setConvos(data); return data; }, []);

  useEffect(() => {
    (async () => {
      const data = await loadConvos();
      const to = params.get("to");
      if (to) {
        const { data: u } = await api.get(`/users/${to}`);
        setActive(u);
      } else if (data.length) setActive(data[0].user);
      setLoading(false);
    })();
  }, [loadConvos, params]);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active.id}`).then(({ data }) => setThread(data));
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const { data } = await api.post("/messages", { to_user: active.id, text });
    setThread((t) => [...t, { ...data, mine: true }]);
    setText("");
    loadConvos();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-6">Messages</h1>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] grid grid-cols-1 md:grid-cols-3 overflow-hidden h-[70vh]">
        <div className="border-r border-slate-100 overflow-y-auto">
          {convos.length === 0 && <p className="p-5 text-sm text-slate-400">No conversations yet. Start one from Discover.</p>}
          {active && !convos.some((c) => c.user.id === active.id) && (
            <ConvoItem u={active} text="New conversation" active onClick={() => {}} />
          )}
          {convos.map((c) => (
            <ConvoItem key={c.user.id} u={c.user} text={c.last_text} active={active?.id === c.user.id} onClick={() => setActive(c.user)} />
          ))}
        </div>

        <div className="md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <MessageCircle className="h-10 w-10 mb-2 text-pink-300" /><p className="font-semibold">Select a conversation</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-slate-100">
                <Avatar className="h-10 w-10 ring-2 ring-pink-100"><AvatarImage src={active.avatar} /><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold">{(active.name||"U")[0]}</AvatarFallback></Avatar>
                <div><p className="font-bold text-slate-900">{active.name}</p><p className="text-xs text-slate-400 capitalize">{active.role}</p></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FDF8FA]">
                {thread.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div data-testid="message-bubble" className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${m.mine ? "brand-gradient text-white rounded-br-md" : "bg-white border border-slate-100 text-slate-700 rounded-bl-md"}`}>{m.text}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…"
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-pink-500/20 outline-none" />
                <button data-testid="message-send" onClick={send} className="h-11 w-11 rounded-full brand-gradient flex items-center justify-center text-white shadow-md"><Send className="h-5 w-5" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConvoItem({ u, text, active, onClick }) {
  return (
    <button data-testid={`convo-${u.id}`} onClick={onClick} className={`w-full flex items-center gap-3 p-4 text-left transition-all ${active ? "bg-pink-50" : "hover:bg-slate-50"}`}>
      <Avatar className="h-11 w-11 ring-2 ring-pink-100"><AvatarImage src={u.avatar} /><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold">{(u.name||"U")[0]}</AvatarFallback></Avatar>
      <div className="min-w-0"><p className="font-bold text-slate-900 truncate">{u.name}</p><p className="text-xs text-slate-400 truncate">{text}</p></div>
    </button>
  );
}
